import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { auth } from '../lib/supabase'
import { invokeFunction } from '../lib/api'
import { offlineStore } from '../lib/offline/store'
import { surveyStore } from '../lib/offline/surveyStore'
import { purgeLegacyLocalStorageDrafts } from '../lib/offline/dailyAssessmentDraft'
import {
    clearProtectedQueryState,
    isIdentityTransition,
    type QueryIdentity,
} from '../lib/queryClient'
import {
    deriveFacilityScope,
    unknownFacilityScope,
    type FacilityScopeStatus,
} from '../lib/auth/facilityScope'
import { getFacilities } from '../lib/services/facilities'
import type { Capability } from '../lib/services/capabilities'
import type { Session, User } from '@supabase/supabase-js'

const INITIAL_SESSION_TIMEOUT_MS = 5_000

export type SubscriptionTier = 'standalone' | 'consultant' | 'enterprise' | 'field_surveyor_solo'

export interface ViewerContext {
    user_id: string
    tenant_id: string
    role: 'Staff' | 'Surveyor' | 'Admin' | 'Reviewer' | 'FieldSurveyor'
    tier: SubscriptionTier
    is_superadmin: boolean
    is_backend_superadmin: boolean
    permissions: string[]
    /** Authoritative facility assignments after hydration (#1897). Empty only when unknown/none. */
    facility_ids: string[]
    /** unknown until loaded; none = zero assignments; concrete/tenant_wide after list. */
    facility_scope_status: FacilityScopeStatus
    granted_capabilities: Capability[]
}

/** Map JWT/app_metadata role strings → ViewerContext.role (exported for unit tests). */
export function mapRole(rawRole: string | undefined): ViewerContext['role'] {
    if (import.meta.env.VITE_FORCE_FIELD_SURVEYOR === 'true') return 'FieldSurveyor'
    const role = (rawRole || '').toLowerCase()
    if (role === 'admin' || role === 'superadmin') return 'Admin'
    if (role === 'reviewer') return 'Reviewer'
    // Seeded / smoke accounts store role as "surveyor"; "editor" is the legacy alias.
    if (role === 'surveyor' || role === 'editor') return 'Surveyor'
    if (role === 'field_surveyor') return 'FieldSurveyor'
    if (role === 'staff' || role === 'user') return 'Staff'
    // Unknown roles default to Staff (least privilege).
    return 'Staff'
}

function mapTier(rawTier: string | undefined): SubscriptionTier {
    const tier = (rawTier || '').toLowerCase()
    if (tier === 'consultant') return 'consultant'
    if (tier === 'enterprise') return 'enterprise'
    if (tier === 'field_surveyor_solo') return 'field_surveyor_solo'
    return 'standalone'
}

function mapPermissions(value: unknown): string[] {
    if (!Array.isArray(value)) return []
    return value
        .filter((permission): permission is string => typeof permission === 'string')
        .map((permission) => permission.trim())
        .filter((permission) => permission.length > 0)
}

export interface AuthContextType {
    session: Session | null
    user: User | null
    viewerContext: ViewerContext | null
    isLoading: boolean
    signIn: (email: string, password: string) => Promise<{ error: Error | null; hasSession: boolean }>
    signOut: () => Promise<void>
}

async function fetchGrantedCapabilities(userId: string): Promise<Capability[]> {
    try {
        const { data } = await invokeFunction<{ capabilities: Array<{ capability: string }> }>('admin-users', {
            method: 'GET',
            path: `/capabilities?user_id=${encodeURIComponent(userId)}`,
        })
        if (data?.capabilities && Array.isArray(data.capabilities)) {
            return data.capabilities.map(g => g.capability as Capability)
        }
    } catch {
        // Non-fatal — user just won't have delegated capabilities
    }
    return []
}

function buildViewerContext(
    session: Session,
    grantedCapabilities: Capability[] = [],
    facilityScope: { facility_ids: string[]; facility_scope_status: FacilityScopeStatus } = unknownFacilityScope(),
): ViewerContext {
    return {
        user_id: session.user.id,
        tenant_id: String(session.user.app_metadata?.tenant_id || 'unknown-tenant'),
        role: mapRole(session.user.app_metadata?.role),
        tier: mapTier(session.user.app_metadata?.subscription_tier),
        is_superadmin: String(session.user.app_metadata?.role || '').toLowerCase() === 'superadmin',
        is_backend_superadmin: session.user.app_metadata?.backend_superadmin === true,
        permissions: mapPermissions(session.user.app_metadata?.permissions),
        facility_ids: facilityScope.facility_ids,
        facility_scope_status: facilityScope.facility_scope_status,
        granted_capabilities: grantedCapabilities,
    }
}

async function fetchFacilityScope(role: ViewerContext['role'], isSuperadmin: boolean) {
    try {
        const facilities = await getFacilities()
        const ids = facilities.map((f) => f.facility_id)
        return deriveFacilityScope(ids, role, isSuperadmin)
    } catch {
        // Fail closed: keep unknown so product does not treat empty as corporate
        return unknownFacilityScope()
    }
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

function sessionIdentity(session: Session | null | undefined): QueryIdentity | null {
    if (!session?.user?.id) return null
    return {
        userId: session.user.id,
        tenantId: String(session.user.app_metadata?.tenant_id || 'unknown-tenant'),
    }
}

export function AuthProvider({
    children,
    queryClient,
}: {
    children: React.ReactNode
    /** Optional for tests; production App passes appQueryClient for F-03 identity clear. */
    queryClient?: QueryClient
}) {
    const [session, setSession] = useState<Session | null>(null)
    const [viewerContext, setViewerContext] = useState<ViewerContext | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const identityRef = useRef<QueryIdentity | null>(null)
    /** Generation token so async capability/facility hydration cannot repopulate after identity change. */
    const enrichGenRef = useRef(0)

    async function onIdentityChange(nextSession: Session | null) {
        const nextId = sessionIdentity(nextSession)
        const prevId = identityRef.current
        if (isIdentityTransition(prevId, nextId)) {
            // Invalidate in-flight grants/facility hydration for prior identity
            enrichGenRef.current += 1
            // F-03: cancel in-flight + clear React Query so A→logout→B cannot reuse A cache
            if (queryClient) {
                await clearProtectedQueryState(queryClient)
            }
            if (!nextId) {
                // Logout / signed-out: wipe offline stores (HIPAA shared-device)
                await Promise.all([
                    offlineStore.wipeAll(),
                    surveyStore.wipeAll(),
                ])
                offlineStore.setActiveOwner(null, null)
                // #1896: drop any residual plaintext daily-assessment localStorage keys
                purgeLegacyLocalStorageDrafts()
            } else if (prevId) {
                // A→B identity switch: wipe so B cannot sync A's queues
                await Promise.all([
                    offlineStore.wipeAll(),
                    surveyStore.wipeAll(),
                ])
                purgeLegacyLocalStorageDrafts()
            } else {
                // Cold login null→user: purge legacy unowned rows (owner bind happens below)
                // Full wipe not required for same-device first login of a single user
                purgeLegacyLocalStorageDrafts()
            }
        }
        identityRef.current = nextId
        // F-04: bind offline store to active owner.
        // Use stable identity-scoped KDF material (not rotating access_token) so refresh
        // does not orphan the user's own ciphertext. Token still never stored in IndexedDB.
        if (nextId) {
            const kdfMaterial = `offline-v1:${nextId.tenantId}:${nextId.userId}`
            offlineStore.setActiveOwner(nextId, kdfMaterial)
            // Cold login and every bind: drop legacy unowned plaintext rows
            void offlineStore.purgeLegacyUnowned()
        } else {
            offlineStore.setActiveOwner(null, null)
        }
    }

    async function enrichViewerContext(activeSession: Session, gen: number) {
        const base = buildViewerContext(activeSession)
        const [grants, facilityScope] = await Promise.all([
            fetchGrantedCapabilities(activeSession.user.id),
            fetchFacilityScope(base.role, base.is_superadmin),
        ])
        // Drop if identity changed while in flight
        if (enrichGenRef.current !== gen) return
        if (sessionIdentity(activeSession)?.userId !== identityRef.current?.userId) return
        setViewerContext(buildViewerContext(activeSession, grants, facilityScope))
    }

    useEffect(() => {
        // 1. Listen for auth changes
        const { data: { subscription } } = auth.onAuthStateChange(async (event, session) => {
            // INITIAL_SESSION fires redundantly before getSession() resolves.
            // Let getSession() handle initialization to avoid a race where
            // isLoading is set false before the session is actually loaded.
            if (event === 'INITIAL_SESSION') return

            await onIdentityChange(session)
            setSession(session)

            if (session) {
                // Immediate context: facility scope unknown until authoritative list loads
                const gen = enrichGenRef.current
                setViewerContext(buildViewerContext(session))
                setIsLoading(false)
                void enrichViewerContext(session, gen)
            } else {
                setViewerContext(null)
                setIsLoading(false)
            }
        })

        // 2. Initial check — do not hang the login shell if Supabase never settles.
        const sessionTimeout = setTimeout(() => setIsLoading(false), INITIAL_SESSION_TIMEOUT_MS)
        auth.getSession()
            .then(async ({ data: { session } }) => {
                await onIdentityChange(session)
                if (session) {
                    setSession(session)
                    const gen = enrichGenRef.current
                    setViewerContext(buildViewerContext(session))
                    void enrichViewerContext(session, gen)
                }
            })
            .catch(async () => {
                await onIdentityChange(null)
                setSession(null)
                setViewerContext(null)
            })
            .finally(() => {
                clearTimeout(sessionTimeout)
                setIsLoading(false)
            })

        return () => {
            clearTimeout(sessionTimeout)
            subscription.unsubscribe()
        }
        // queryClient is stable from App; intentional mount-once auth subscription
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryClient])

    const signOut = async () => {
        // Clear query cache + offline before signOut completes (F-03 / F-04)
        await onIdentityChange(null)
        await auth.signOut()
        setSession(null)
        setViewerContext(null)
    }

    const signIn = async (email: string, password: string) => {
        const { data, error } = await auth.signInWithPassword({ email, password })
        if (error) {
            setIsLoading(false)
            return { error, hasSession: false }
        }

        if (data.session) {
            await onIdentityChange(data.session)
            setSession(data.session)
            const gen = enrichGenRef.current
            setViewerContext(buildViewerContext(data.session))
            // Critical for post-login navigation: ProtectedRoute must not keep the
            // full-screen spinner if INITIAL getSession is still in flight.
            setIsLoading(false)
            // Backfill capabilities + facility scope (generation-guarded)
            void enrichViewerContext(data.session, gen)
            return { error: null, hasSession: true }
        }

        setIsLoading(false)
        return { error: null, hasSession: false }
    }

    return (
        <AuthContext.Provider value={{
            session,
            user: session?.user ?? null,
            viewerContext,
            isLoading,
            signIn,
            signOut
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
